import { z } from "zod";

/**
 * Schema change policy and the per-table schema change log (product gaps row 7; design
 * `docs/research/2026-09-28-design-schema-policy.md`, owner answers of 2026-09-28).
 *
 * A connection's `schema_policy` says what the load does when the source's columns change. The
 * log (`schema_changes`, instance `0007`) holds one row per change to one column of one table:
 * names and types only, never a value (SEC-17). A person decides on a row in the product; the
 * runner carries the decision out on the next load of that table. Nothing here runs DDL.
 */

/** Stored modes, in the order the connection screen lists them. */
export const SCHEMA_POLICY_MODES = ["allow_all", "allow_columns", "review"] as const;
export type SchemaPolicyMode = (typeof SCHEMA_POLICY_MODES)[number];

/**
 * Owner answer W3-4: a new connection allows new columns, which is exactly what the load did
 * before the policy existed. Per connection only (W3-5): there is no instance-wide default.
 */
export const DEFAULT_SCHEMA_POLICY: SchemaPolicyMode = "allow_columns";

/** The label and the one help line each mode shows. */
export const SCHEMA_POLICY_LABELS: Record<SchemaPolicyMode, { label: string; help: string }> = {
  allow_all: {
    label: "Allow all changes",
    help: "New columns are added. A type the table cannot hold is widened on its own, which rewrites the table.",
  },
  allow_columns: {
    label: "Allow new columns only",
    help: "New columns are added. A type the table cannot hold stops the table until you widen the column or rebuild the table.",
  },
  review: {
    label: "Review every change",
    help: "New columns are held back until someone approves them. The table keeps loading its known columns.",
  },
};

export const SCHEMA_CHANGE_KINDS = ["added", "dropped", "type_changed", "key_lost"] as const;
export type SchemaChangeKind = (typeof SCHEMA_CHANGE_KINDS)[number];

/**
 * Round 6 (tracker T-02, instance `0014`): which side saw a change first. `load` is the load's own
 * comparison of the staged columns with the target (every row before round 6). `connector` is
 * the SQL extractor v1.7.0's schema fingerprint, for a change the load cannot see because the
 * warehouse type did not move (smallint to bigint, varchar to nvarchar); such a row is `applied`,
 * with nothing to decide.
 */
export const SCHEMA_CHANGE_SOURCES = ["load", "connector"] as const;
export type SchemaChangeSource = (typeof SCHEMA_CHANGE_SOURCES)[number];

/**
 * A row's status (design section 4.2), plus `pending`: a dropped column inside its grace, which
 * is not yet a change (section 4.6) and is never listed.
 */
export const SCHEMA_CHANGE_STATUSES = [
  "pending",
  "applied",
  "waiting",
  "approved",
  "ignored",
  "stopped",
  "widen",
  "rebuild",
  "done",
] as const;
export type SchemaChangeStatus = (typeof SCHEMA_CHANGE_STATUSES)[number];

/** Rows a person has to decide on. The connection badge and message bar count these. */
export const DECISION_NEEDED_STATUSES: readonly SchemaChangeStatus[] = ["waiting", "stopped"];
/** Rows decided and waiting for the next load of their table to carry the decision out. */
export const DECIDED_PENDING_LOAD_STATUSES: readonly SchemaChangeStatus[] = [
  "approved",
  "widen",
  "rebuild",
];
/** What `status=open` lists: needs a decision, or decided and not yet carried out. */
export const OPEN_SCHEMA_CHANGE_STATUSES: readonly SchemaChangeStatus[] = [
  ...DECISION_NEEDED_STATUSES,
  ...DECIDED_PENDING_LOAD_STATUSES,
];

export const SCHEMA_DECISIONS = [
  "approve",
  "ignore",
  "unignore",
  "acknowledge",
  "widen",
  "rebuild",
] as const;
export type SchemaDecision = (typeof SCHEMA_DECISIONS)[number];

/** The most change ids one decide call names. */
export const SCHEMA_DECIDE_MAX = 200;

/** Owner answer W3-8: Widen and Rebuild are the only two fixes for a type change. */
const FITS: Record<
  SchemaDecision,
  { kinds: readonly SchemaChangeKind[]; status: SchemaChangeStatus }
> = {
  approve: { kinds: ["added"], status: "waiting" },
  ignore: { kinds: ["added"], status: "waiting" },
  unignore: { kinds: ["added"], status: "ignored" },
  acknowledge: { kinds: ["dropped"], status: "waiting" },
  widen: { kinds: ["type_changed"], status: "stopped" },
  rebuild: { kinds: ["type_changed", "key_lost"], status: "stopped" },
};

/** True when `decision` applies to a row of this kind in this status (else 422 on the route). */
export function decisionFits(
  change: { kind: SchemaChangeKind; status: SchemaChangeStatus },
  decision: SchemaDecision,
): boolean {
  const rule = FITS[decision];
  return rule.status === change.status && rule.kinds.includes(change.kind);
}

/** The decisions every one of `changes` fits: what the change log drawer offers. */
export function fittingDecisions(
  changes: ReadonlyArray<{ kind: SchemaChangeKind; status: SchemaChangeStatus }>,
): SchemaDecision[] {
  if (changes.length === 0) return [];
  return SCHEMA_DECISIONS.filter((decision) =>
    changes.every((change) => decisionFits(change, decision)),
  );
}

/**
 * The status a row takes when the decision is recorded. Acknowledge makes a dropped column a
 * standing `applied` row: nothing to carry out, and it is not logged again while it stays gone.
 */
export function decisionStatus(decision: SchemaDecision): SchemaChangeStatus {
  switch (decision) {
    case "approve":
      return "approved";
    case "ignore":
      return "ignored";
    case "unignore":
      return "waiting";
    case "acknowledge":
      return "applied";
    case "widen":
      return "widen";
    case "rebuild":
      return "rebuild";
  }
}

/** `PUT /dataflows/:id/schema-policy`. `applyWaiting` confirms approving held columns (409 otherwise). */
export const schemaPolicyBodySchema = z
  .object({
    mode: z.enum(SCHEMA_POLICY_MODES),
    applyWaiting: z.boolean().default(false),
  })
  .strict();
export type SchemaPolicyBody = z.input<typeof schemaPolicyBodySchema>;

export const SCHEMA_CHANGE_PAGE_SIZES = [10, 20, 50] as const;

/** `GET /dataflows/:id/schema-changes`: one table or all, open or all, 20 a page by default. */
export const schemaChangesQuerySchema = z
  .object({
    entity: z.string().trim().min(1).max(400).optional(),
    /** #1663: only the changes this run first saw (the run page's link). */
    run: z.string().trim().min(1).max(64).optional(),
    status: z.enum(["open", "all"]).default("open"),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .refine((value) => (SCHEMA_CHANGE_PAGE_SIZES as readonly number[]).includes(value), {
        message: "pageSize is 10, 20 or 50.",
      })
      .default(20),
  })
  .strict();
export type SchemaChangesQuery = z.infer<typeof schemaChangesQuerySchema>;

/** `POST /dataflows/:id/schema-changes/decide`. `runNow` also needs `runs.start`. */
export const decideSchemaChangesBodySchema = z
  .object({
    changeIds: z.array(z.uuid()).min(1).max(SCHEMA_DECIDE_MAX),
    decision: z.enum(SCHEMA_DECISIONS),
    runNow: z.boolean().default(false),
  })
  .strict();
export type DecideSchemaChangesBody = z.input<typeof decideSchemaChangesBodySchema>;

/** One row of the log as the API answers it: names and types, never a value. */
export interface SchemaChangeView {
  id: string;
  dataflowId: string;
  entityName: string;
  targetTable: string;
  columnName: string;
  sourceColumn: string | null;
  kind: SchemaChangeKind;
  typeBefore: string | null;
  typeAfter: string | null;
  status: SchemaChangeStatus;
  seenCount: number;
  firstSeenRunId: string;
  lastSeenRunId: string;
  firstSeenAt: string;
  lastSeenAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  doneRunId: string | null;
  /** Round 6 (T-02): the side that saw the change. Always sent; absent only from an older server. */
  detectedBy?: SchemaChangeSource;
}

/** `GET /schema-changes/summary`: open counts per table, for the badges and the message bar. */
export interface SchemaChangeTableCounts {
  dataflowId: string;
  entityName: string;
  /** New columns held for review, and dropped columns to acknowledge. */
  waiting: number;
  /** Type changes and lost key columns that stop the table. */
  stopped: number;
  /** Decided and waiting for the table's next load. */
  queued: number;
}

export interface SetSchemaPolicyResult {
  mode: SchemaPolicyMode;
  previous: SchemaPolicyMode;
  /** Held columns approved by this switch (`applyWaiting`). */
  approvedWaiting: number;
}

export interface DecideSchemaChangesResult {
  decision: SchemaDecision;
  /** Rows the decision moved, including the other open rows of a rebuilt table. */
  changed: number;
  tableCount: number;
  /** The run started with `runNow`, or null. */
  runId: string | null;
  /**
   * With `runNow`, when the run start was refused (the connection busy, disabled or quiesced):
   * the refusal's error code and message. The decision is saved either way, and the next import
   * carries it out.
   */
  runNotStarted?: { code: string; message: string };
}

/**
 * The load's per-table counts on `metrics.entities[].schemaChanges` (design section 4.4, item 7):
 * only changes FIRST SEEN in that run, so a column waiting for review warns once, not nightly.
 */
export interface SchemaChangeCounts {
  added: number;
  waiting: number;
  widened: number;
  stopped: number;
  /**
   * #1663: a column the table has and the source does not send, first logged in this run as
   * applied (kept, not loaded), or a pending one surfacing after its grace. Under `review` such a
   * column is counted in `waiting` instead, so one column is never counted twice.
   */
  notSent: number;
  /** #1663: a type change only the connector's fingerprint saw (`detected_by = connector`). */
  retyped: number;
}

/** #1663: counts with nothing in them. */
export function emptySchemaChangeCounts(): SchemaChangeCounts {
  return { added: 0, waiting: 0, widened: 0, stopped: 0, notSent: 0, retyped: 0 };
}

/**
 * #1663: the changes a run message tells (every count but `stopped`, which is the table's
 * failure and is counted there).
 */
export function toldSchemaChanges(counts: Omit<SchemaChangeCounts, "stopped">): number {
  return counts.added + counts.waiting + counts.widened + counts.notSent + counts.retyped;
}

/** The run events the load writes beside `LOAD_SCHEMA_DRIFT` (counts and column names only). */
export const SCHEMA_EVENTS = {
  held: "LOAD_SCHEMA_HELD",
  widened: "LOAD_SCHEMA_WIDENED",
  rebuilt: "LOAD_SCHEMA_REBUILT",
} as const;
